import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { FOUNDER_IMAGE, TEAM_IMAGE } from "@/lib/images";
import useSeo from "@/hooks/useSeo";
import Reveal from "@/components/Reveal";
import SectionOrnament from "@/components/SectionOrnament";
import { PageHero } from "@/components/PageHero";

export default function About() {
  const { t, lang } = useLanguage();
  const { content } = useContent();
  const about = content.about || {};
  const values = about.values || [];

  useSeo("Über uns", "À propos", "Lernt M. Koko und Our Promise Event kennen – Vision, Werte und Team.", "Découvrez M. Koko et Our Promise Event – vision, valeurs et équipe.");

  const paragraphs = t("about.paragraphs");
  const teamParagraphs = t("about.teamParagraphs");

  return (
    <div data-testid="about-page">
      <PageHero overline={t("about.overline")} title={t("about.founderTitle")} />

      {/* Founder */}
      <section className="relative bg-cream py-20 sm:py-28 px-6 lg:px-10 overflow-hidden">
        <SectionOrnament variant="corners" />
        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-5 gap-14 items-start">
          <Reveal className="lg:col-span-2 lg:sticky lg:top-28">
            <div className="arched-frame w-full max-w-sm mx-auto aspect-[3/4] zoom-parent bg-champagne/40">
              <img src={FOUNDER_IMAGE} alt={about.name || "M. Koko"} loading="lazy" className="w-full h-full object-cover zoom-img" />
            </div>
            <div className="text-center mt-6">
              <p className="font-heading text-2xl">{t("about.founderName")}</p>
              <p className="text-xs tracking-[0.25em] uppercase text-gold mt-1">{t("about.founderRole")}</p>
            </div>
          </Reveal>

          <div className="lg:col-span-3 space-y-6">
            {Array.isArray(paragraphs) && paragraphs.map((p, i) => (
              <Reveal key={i} delay={i * 60}>
                <p className={`leading-relaxed font-light ${i === 0 ? "font-heading text-2xl sm:text-3xl italic text-ink" : "text-ink-muted text-base sm:text-lg"}`}>
                  {p}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative bg-secondary/40 py-20 sm:py-28 px-6 lg:px-10 overflow-hidden">
        <SectionOrnament variant="dots" />
        <div className="relative max-w-5xl mx-auto text-center">
          <Reveal><p className="text-xs tracking-[0.35em] uppercase text-gold mb-6">{t("about.valuesTitle")}</p></Reveal>
          <SectionOrnament variant="divider" className="mb-12" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <Reveal key={i} delay={i * 70} className="border-t border-gold/40 pt-5">
                <p className="font-heading text-xl sm:text-2xl font-light">{lang === "fr" ? v.fr : v.de}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="relative bg-cream py-20 sm:py-28 px-6 lg:px-10 overflow-hidden">
        <SectionOrnament variant="corners" />
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Reveal><p className="text-xs tracking-[0.35em] uppercase text-gold mb-4">{t("about.teamTitle")}</p></Reveal>
            <Reveal delay={60}><p className="max-w-3xl mx-auto text-ink-muted font-light leading-relaxed">{t("about.teamIntro")}</p></Reveal>
          </div>
          <Reveal delay={100} className="zoom-parent aspect-[16/9] max-w-4xl mx-auto bg-champagne/40 border border-border mb-12">
            <img src={TEAM_IMAGE} alt={t("about.teamTitle")} loading="lazy" className="w-full h-full object-cover zoom-img" />
          </Reveal>
          <div className="max-w-3xl mx-auto space-y-6">
            {Array.isArray(teamParagraphs) && teamParagraphs.map((p, i) => (
              <Reveal key={i} delay={i * 60}><p className="text-ink-muted font-light leading-relaxed text-base sm:text-lg">{p}</p></Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary/40 py-16 px-6 text-center">
        <Reveal>
          <Link to="/termin" className="inline-block bg-ink text-cream px-9 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-gold transition-colors duration-500">
            {t("nav.cta")}
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
